import { v4 as uuid } from 'uuid'
import { minus, plus, times, divide } from 'number-precision'
import { BET_RESULT } from 'constants/index'
import { calculateDiceOdds } from './utils/math.utils'

export const DICE_NUMBER_HARD_MIN = 0.01
export const DICE_NUMBER_HARD_MAX = 99.99

export const DEMO_DICE_GAME_SETTINGS = {
  id: '5',
  gameId: 5,
  minBet: { USD: 0.5, EUR: 0.5, IDR: 10 },
  maxBet: { USD: 20, EUR: 20, IDR: 200000 },
  maxProfit: { USD: 1000, EUR: 1000, IDR: 1900000 },
  houseEdge: 4,
  minOdds: 1.01,
  maxOdds: 49.5,
  minAutoRate: 1.01
}

const trunc2 = (v) => Math.floor(v * 100 + 1e-9) / 100

export const sanitiseDiceSettings = (settings) => {
  const s = settings || DEMO_DICE_GAME_SETTINGS

  const rawEdge = Number(s.houseEdge)
  const rawMin = Number(s.minOdds)
  const rawMax = Number(s.maxOdds)

  const houseEdge = Number.isFinite(rawEdge) && rawEdge >= 0 && rawEdge < 100 ? rawEdge : DEMO_DICE_GAME_SETTINGS.houseEdge
  const minOdds = Number.isFinite(rawMin) && rawMin > 1 ? rawMin : 1.01
  const maxOdds = Number.isFinite(rawMax) && rawMax > minOdds ? rawMax : 49.5

  return { ...s, houseEdge, minOdds, maxOdds }
}

export const getDiceNumberBounds = (settings, rollOver) => {
  const { minOdds, maxOdds, houseEdge } = sanitiseDiceSettings(settings)
  const rtp = minus(1, divide(houseEdge, 100))

  const chanceLo = times(divide(rtp, maxOdds), 100)
  const chanceHi = times(divide(rtp, minOdds), 100)

  const gridLo = Math.floor(chanceLo * 100) / 100
  const gridHi = Math.floor(chanceHi * 100) / 100

  const rawMin = rollOver ? 100 - gridHi : gridLo
  const rawMax = rollOver ? 100 - gridLo : gridHi

  const min = Math.max(DICE_NUMBER_HARD_MIN, Math.round(rawMin * 100) / 100)
  const max = Math.min(DICE_NUMBER_HARD_MAX, Math.round(rawMax * 100) / 100)

  const a = rollOver ? 100 - min : min
  const b = rollOver ? 100 - max : max

  return { min, max, chanceMin: Math.min(a, b), chanceMax: Math.max(a, b) }
}

const pickCurrencyValue = (map, currencyCode) => {
  if (!map || typeof map !== 'object' || !currencyCode) return NaN
  return parseFloat(map[currencyCode])
}

export const diceGameDemoPlaceBet = function ({
  number,
  rollOver,
  betAmount,
  demoBalance,
  currencyCode,
  settings
}) {
  const config = sanitiseDiceSettings(settings)
  const { minBet, maxBet, maxProfit, houseEdge, minOdds, maxOdds } = config

  const num = Math.round(Number(number) * 100) / 100
  const amount = Number(betAmount)
  const { min: numberMin, max: numberMax } = getDiceNumberBounds(config, rollOver)

  if (!Number.isFinite(num) || num < numberMin || num > numberMax) {
    throw new Error('InvalidDiceNumber')
  }

  const minBetAmount = pickCurrencyValue(minBet, currencyCode)
  const maxBetAmount = pickCurrencyValue(maxBet, currencyCode)
  const maxProfitAmount = pickCurrencyValue(maxProfit, currencyCode)

  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('InvalidBetAmount')
  }
  if (Number.isFinite(minBetAmount) && amount < minBetAmount) {
    throw new Error('BetAmountBelowMinimum')
  }
  if (Number.isFinite(maxBetAmount) && amount > maxBetAmount) {
    throw new Error('BetAmountAboveMaximum')
  }
  if (Number.isFinite(Number(demoBalance)) && amount > Number(demoBalance)) {
    throw new Error('InsufficientBalance')
  }

  const diceGameResult = Math.floor(Math.random() * 1e4) / 100

  let winningAmount = 0
  let betResult = BET_RESULT.LOST
  const probability = rollOver ? (100 - num) / 100 : num / 100

  if ((rollOver && diceGameResult > num) || (!rollOver && diceGameResult < num)) {
    // Truncated to 2dp so the paid multiplier matches the one shown in the UI.
    const odds = trunc2(
      calculateDiceOdds({ houseEdge, minOdd: minOdds, maxOdd: maxOdds }, 1 / probability)
    )

    betResult = BET_RESULT.WON
    winningAmount = times(amount, odds)

    const profit = minus(winningAmount, amount)
    if (Number.isFinite(maxProfitAmount) && profit > maxProfitAmount) {
      winningAmount = plus(amount, maxProfitAmount)
    }
  }

  return {
    id: uuid(),
    number: num,
    winningNumber: diceGameResult,
    rollOver,
    betAmount: amount,
    winningAmount,
    multiplier: winningAmount ? trunc2(winningAmount / amount) : 0,
    result: betResult
  }
}
